import React from "react";
import { Box, Paper, Typography, Grid, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import { People, Description } from "@mui/icons-material";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";

import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreateClassDialog from "./CreateClassDialog";
import { db } from "./firebase";
import { formatDate } from "./functions";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

const MyClasses = ({ user, styles, theme }) => {
  const [classesWithEssayCounts, setClassesWithEssayCounts] = React.useState(
    [],
  );
  const [loading, setLoading] = React.useState(true);
  const [isCreateClassDialogOpen, setIsCreateClassDialogOpen] =
    React.useState(false);

  React.useEffect(() => {
    fetchClassesAndEssays();
  }, [user.uid]);

  const fetchClassesAndEssays = async () => {
    try {
      const classesQuery = query(
        collection(db, "classes"),
        where("userId", "==", user.uid),
      );
      const classesSnapshot = await getDocs(classesQuery);
      const classesData = classesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        essayCount: 0,
        reviewedCount: 0,
      }));

      // Updated to include reviewed count
      const classesWithEssays = await Promise.all(
        classesData.map(async (classItem) => {
          const essaysQuery = query(
            collection(db, "essays"),
            where("classId", "==", classItem.id),
            orderBy("createdAt", "desc"),
          );
          const essaysSnapshot = await getDocs(essaysQuery);
          const essays = essaysSnapshot.docs;
          const reviewedEssays = essays.filter((doc) => doc.data().reviewed);

          return {
            ...classItem,
            essayCount: essays.length,
            reviewedCount: reviewedEssays.length,
          };
        }),
      );

      setClassesWithEssayCounts(classesWithEssays);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClassCreated = async (newClass) => {
    await fetchClassesAndEssays();
    setIsCreateClassDialogOpen(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.innerContainer}>
        <Typography
          variant="h1"
          sx={{ ...styles.header, borderBottom: "0px", mb: 6 }}
        >
          Mina klasser
        </Typography>
        {classesWithEssayCounts.length === 0 && (
          <Typography variant="body1" sx={{ mt: 2 }}>
            Du har inga klasser än. Skapa en ny klass genom att klicka på "Lägg
            till klass" ovan.
          </Typography>
        )}
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            sx={{ ...styles.tightButton }}
            startIcon={<AddIcon />}
            onClick={() => setIsCreateClassDialogOpen(true)}
          >
            Lägg till klass
          </Button>
        </Box>

        <Grid container spacing={3}>
          {classesWithEssayCounts.map((classItem) => (
            <Grid item xs={12} sm={6} md={4} key={classItem.id}>
              <Link
                to={`/app/klasser/${classItem.id}`}
                style={{ textDecoration: "none" }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "scale(1.03)",
                    },

                    borderRadius: 3,
                    boxShadow: "0 3px 28px rgba(12,8,51,.06)",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "600" }}
                    component="div"
                    gutterBottom
                  >
                    {classItem.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Skapad {formatDate(classItem.createdAt)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <People sx={{ mr: 1, fontSize: 20, color: "#1976d2" }} />

                    <Typography variant="body2">
                      {classItem.studentCount} elever
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Description
                      sx={{ mr: 1, fontSize: 20, color: "#ed6c02" }}
                    />

                    <Typography variant="body2">
                      {classItem.essayCount} bedömningar
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <TaskAltIcon
                      sx={{ mr: 1, fontSize: 20, color: "#2E7D32" }}
                    />
                    <Typography variant="body2">
                      {classItem.reviewedCount}/{classItem.essayCount} granskade
                    </Typography>
                  </Box>
                </Paper>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Box>
      <CreateClassDialog
        open={isCreateClassDialogOpen}
        onClose={() => setIsCreateClassDialogOpen(false)}
        onClassCreated={handleClassCreated}
        userId={user.uid}
      />
    </Box>
  );
};

export default MyClasses;
